'use client';

// Replace direct imports with lazy imports
import { Suspense, lazy } from 'react';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Icons,
} from '@/components';
import { MAX_CHARS, useBiasDetection } from '@/context/app-context';

import { cn } from '@/lib/utils';

// Non-Lazy Loading Implementation for Testing
// import { BertVisualization } from './components/bert-visualization';
// import { BiasLegend } from './components/bias-legend';
// import { GuidedTour } from './components/guided-tour';
// import { HelperButtons } from './components/helper-buttons';
// import { HighlightedText } from './components/highlighted-text';
// import { HistoryPanel } from './components/history-panel';
// import { UserInput } from './components/user-input';
// import { XAIExplanations } from './components/xai-explanations';
// import { XAIMethodComparison } from './components/xai-method-comparison';

// Lazy Loading Implementation for Production
import { BiasLegend } from './components/bias-legend';
import { GuidedTour } from './components/guided-tour';
import { HelperButtons } from './components/helper-buttons';
import { HighlightedText } from './components/highlighted-text';
import { HistoryPanel } from './components/history-panel';
import { UserInput } from './components/user-input';

const BertVisualization = lazy(() =>
    import('./components/bert-visualization').then((mod) => ({
        default: mod.BertVisualization,
    }))
);
const XAIExplanations = lazy(() =>
    import('./components/xai-explanations').then((mod) => ({
        default: mod.XAIExplanations,
    }))
);
const XAIMethodComparison = lazy(() =>
    import('./components/xai-method-comparison').then((mod) => ({
        default: mod.XAIMethodComparison,
    }))
);

export function BiasDetectionPage() {
    const {
        text,
        biasAnalysis,
        combinedExplanation,
        isAnalyzing,
        progress,
        sensitivity,
        biasFilter,
        searchTerm,
        showHistory,
        showGuidedTour,
        analysisHistory,
        onDetectBias,
        handleFileContent,
        handleTextChange,
        handleReset,
        handleTest,
        setSensitivity,
        setBiasFilter,
        handleExport,
        setShowHistory,
        setShowGuidedTour,
        clearHistory,
        bertVisualizationOutput,
        bertMultiLabelOutput,
        onExplainBias,
        isExplaining,
    } = useBiasDetection();

    const analysisAvailable = biasAnalysis.biasedWords.length > 0;
    const explanationAvailable =
        analysisAvailable &&
        bertVisualizationOutput &&
        bertMultiLabelOutput &&
        combinedExplanation;

    return (
        <>
            <div className="container mx-auto p-4">
                <UserInput
                    handleFileContent={handleFileContent}
                    handleTextChange={handleTextChange}
                    analyzeText={onDetectBias}
                    handleReset={handleReset}
                    handleTest={handleTest}
                    isAnalyzing={isAnalyzing}
                    progress={progress}
                    sensitivity={sensitivity}
                    setSensitivity={setSensitivity}
                    biasFilter={biasFilter}
                    setBiasFilter={setBiasFilter}
                    searchTerm={searchTerm}
                    maxChars={MAX_CHARS}
                    setShowGuidedTour={setShowGuidedTour}
                />

                {analysisAvailable && (
                    <>
                        <Card className="mb-2 md:col-span-2">
                            <CardHeader>
                                <CardTitle className="flex justify-between items-center">
                                    <span>Analysis Result</span>
                                    <HelperButtons
                                        handleExport={handleExport}
                                        setShowHistory={setShowHistory}
                                        setShowGuidedTour={setShowGuidedTour}
                                    />
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <BiasLegend
                                    biasFilter={biasFilter}
                                    setBiasFilter={setBiasFilter}
                                />
                                <HighlightedText
                                    text={text}
                                    biasedWords={biasAnalysis.biasedWords}
                                    biasFilter={biasFilter}
                                />
                            </CardContent>
                        </Card>

                        <div className="my-4 flex justify-center">
                            <Button
                                size="lg"
                                className={cn({
                                    'animate-pulse': isExplaining,
                                })}
                                onClick={() => {
                                    !isExplaining && onExplainBias();
                                }}
                            >
                                <Icons.search className="mr-2 h-5 w-5" />
                                {isExplaining
                                    ? 'Generating Explanation...'
                                    : 'Explain Bias Analysis'}
                            </Button>
                        </div>
                    </>
                )}

                {explanationAvailable && (
                    <>
                        <Card className="mb-4">
                            <CardHeader>
                                <CardTitle>
                                    Explainable AI based Explanations
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Suspense
                                    fallback={
                                        <div>Loading explanations...</div>
                                    }
                                >
                                    <XAIExplanations
                                        combinedExplanation={
                                            combinedExplanation
                                        }
                                    />
                                </Suspense>
                            </CardContent>
                        </Card>

                        <Suspense
                            fallback={<div>Loading method comparison...</div>}
                        >
                            <XAIMethodComparison
                                data={combinedExplanation.comparison}
                            />
                        </Suspense>

                        <Suspense
                            fallback={<div>Loading visualization...</div>}
                        >
                            <BertVisualization
                                bertOutputData={bertVisualizationOutput}
                                multiLabelData={bertMultiLabelOutput}
                            />
                        </Suspense>
                    </>
                )}

                <HistoryPanel
                    analysisHistory={analysisHistory}
                    open={showHistory}
                    onClose={() => setShowHistory(false)}
                    clearHistory={() => clearHistory()}
                />

                <GuidedTour
                    open={showGuidedTour}
                    onClose={() => setShowGuidedTour(false)}
                />
            </div>
        </>
    );
}
